import { type ItemStyles, Rating, ThinStar } from '@smastrom/react-rating';
import { type FC, lazy, Suspense } from 'react';
import { StarHalf } from 'react-bootstrap-icons';

import type { RatingsView } from '../../../../modules/interfaces/dashboard';
import { formatCount, formatPercent } from '../../../../modules/utils/dashboard';
import { WidgetCard } from './WidgetCard';

/** Keeps recharts out of the main bundle — see RatingDistributionChart. */
const RatingDistributionChart = lazy(() => import('./RatingDistributionChart'));

const DISTRIBUTION_HEIGHT = 168;

import '@smastrom/react-rating/style.css';

export interface RatingsCardProps {
  ratings?: RatingsView;
  loading: boolean;
}

/**
 * #d97706 rather than the #F19204 used in StayDetails: that amber is 2.37:1 on
 * white, below the 3:1 floor for a filled mark. This one clears it at 3.19:1.
 */
const STAR_STYLES: ItemStyles = {
  itemShapes: ThinStar,
  activeFillColor: '#d97706',
  inactiveFillColor: '#ffffff',
  activeStrokeColor: '#d97706',
  inactiveStrokeColor: '#c9c9c9',
  itemStrokeWidth: 2,
};

export const RatingsCard: FC<RatingsCardProps> = ({ ratings, loading }) => {
  const hasRatings = Boolean(ratings?.hasRatings);
  const percentRated = ratings?.percentRated ?? 0;

  return (
    <WidgetCard
      title="Guest Rating"
      subtitle="All time"
      loading={loading}
      isEmpty={!loading && !hasRatings}
      emptyIcon={<StarHalf size={28} />}
      emptyMessage={
        ratings && ratings.processedCount > 0
          ? `No guest ratings yet — 0 of ${formatCount(ratings.processedCount)} completed stays have been rated.`
          : 'No guest ratings yet.'
      }
      placeholderHeight={320}
    >
      {ratings && (
        <>
          <div className="dashboard-ratings__average">
            <span className="dashboard-ratings__score">
              {ratings.averageRating.toFixed(1)}
            </span>
            <Rating
              style={{ maxWidth: 128 }}
              value={ratings.averageRating}
              itemStyles={STAR_STYLES}
              readOnly
            />
            <span className="dashboard-ratings__out-of">out of 5</span>
          </div>

          <div className="dashboard-ratings__meter-block">
            <span className="dashboard-ratings__meter-caption">
              {formatCount(ratings.ratedCount)} of {formatCount(ratings.processedCount)}{' '}
              stays rated ({formatPercent(percentRated)})
            </span>
            {/*
              The caption above already states both counts and the percentage,
              so the bar is a visual echo — hidden rather than announced twice.
            */}
            <div className="dashboard-meter" aria-hidden="true">
              <div
                className="dashboard-meter__fill"
                style={{ width: `${Math.min(Math.max(percentRated, 0), 100)}%` }}
              />
            </div>
          </div>

          <p className="dashboard-ratings__spread-title">Distribution of rated stays</p>
          <Suspense
            fallback={
              <div
                className="dashboard-card__placeholder"
                style={{ height: DISTRIBUTION_HEIGHT }}
                aria-hidden="true"
              />
            }
          >
            <RatingDistributionChart spread={ratings.spread} />
          </Suspense>
        </>
      )}
    </WidgetCard>
  );
};
